import { CButton, CCard, CCardBody, CCollapse, CFormInput, CModalBody, CModalFooter, CModalHeader } from '@coreui/react'
import React, { forwardRef, useCallback, useContext, useImperativeHandle, useState } from 'react'
import { useComputed } from '~/Resources/util.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { observer } from 'mobx-react-lite'
import { capitalize } from 'lodash-es'
import { CModalExt } from '~/Components/CModalExt.js'
import { go as fuzzySearch } from 'fuzzysort'
import type { ObservableMap } from 'mobx'
import type { EntityModelType, FeedbackEntitySubType } from '@companion-app/shared/Model/EntityModel.js'
import type { ClientEntityDefinition } from '@companion-app/shared/Model/EntityDefinitionModel.js'
import { canAddEntityToFeedbackList } from '@companion-app/shared/Entity.js'

interface AddEntitiesModalProps {
	addEntity: (connectionId: string, definitionId: string) => void
	feedbackListType: FeedbackEntitySubType | null
	entityType: EntityModelType
	entityTypeLabel: string
}
export interface AddEntitiesModalRef {
	show(): void
}

export const AddEntitiesModal = observer(
	forwardRef<AddEntitiesModalRef, AddEntitiesModalProps>(function AddFeedbacksModal(
		{ addEntity, feedbackListType, entityType, entityTypeLabel },
		ref
	) {
		const { connections, entityDefinitions } = useContext(RootAppStoreContext)

		const definitions = entityDefinitions.getEntityDefinitionsStore(entityType)
		const recentlyUsed = entityDefinitions.getRecentlyUsedEntityDefinitionsStore(entityType)

		const [show, setShow] = useState(false)
		const [filter, setFilter] = useState('')

		const doClose = useCallback(() => setShow(false), [])
		const onClosed = useCallback(() => {
			setFilter('')
		}, [])

		useImperativeHandle(
			ref,
			() => ({
				show() {
					setShow(true)
					setFilter('')
				},
			}),
			[]
		)

		const [expanded, setExpanded] = useState<Record<string, boolean>>({})
		const toggleExpanded = useCallback((id: string) => {
			setExpanded((oldVal) => {
				return {
					...oldVal,
					[id]: !oldVal[id],
				}
			})
		}, [])

		const addAndTrackRecentUsage = useCallback(
			(connectionAndDefinitionId: string) => {
				recentlyUsed.trackId(connectionAndDefinitionId)

				const [connectionId, definitionId] = connectionAndDefinitionId.split(':', 2)
				addEntity(connectionId, definitionId)
			},
			[recentlyUsed, addEntity]
		)

		return (
			<CModalExt visible={show} onClose={doClose} onClosed={onClosed} size="lg" scrollable={true}>
				<CModalHeader closeButton>
					<h5>Browse {capitalize(entityTypeLabel)}s</h5>
				</CModalHeader>
				<CModalHeader closeButton={false}>
					<CFormInput
						type="text"
						placeholder="Search ..."
						onChange={(e) => setFilter(e.currentTarget.value)}
						value={filter}
						style={{ fontSize: '1.2em' }}
					/>
				</CModalHeader>
				<CModalBody>
					<ConnectionCollapse
						connectionId="internal"
						items={definitions.connections.get('internal')}
						itemName={`${entityTypeLabel}s`}
						expanded={!!filter || expanded['internal']}
						filter={filter}
						feedbackListType={feedbackListType}
						doToggle={toggleExpanded}
						doAdd={addAndTrackRecentUsage}
					/>
					{connections.sortedConnections().map((connectionInfo) => (
						<ConnectionCollapse
							key={connectionInfo.id}
							connectionId={connectionInfo.id}
							items={definitions.connections.get(connectionInfo.id)}
							itemName={`${entityTypeLabel}s`}
							expanded={!!filter || expanded[connectionInfo.id]}
							filter={filter}
							feedbackListType={feedbackListType}
							doToggle={toggleExpanded}
							doAdd={addAndTrackRecentUsage}
						/>
					))}
				</CModalBody>
				<CModalFooter>
					<CButton color="secondary" onClick={doClose}>
						Done
					</CButton>
				</CModalFooter>
			</CModalExt>
		)
	})
)

interface ConnectionItem {
	fullId: string
	label: string
	description: string | undefined
}

interface ConnectionCollapseProps {
	connectionId: string
	items: ObservableMap<string, ClientEntityDefinition> | undefined
	itemName: string
	expanded: boolean
	filter: string
	feedbackListType: FeedbackEntitySubType | null
	doToggle: (connectionId: string) => void
	doAdd: (itemId: string) => void
}

const ConnectionCollapse = observer(function ConnectionCollapse({
	connectionId,
	items,
	itemName,
	expanded,
	filter,
	feedbackListType,
	doToggle,
	doAdd,
}: ConnectionCollapseProps) {
	const { connections } = useContext(RootAppStoreContext)

	const connectionInfo = connections.getInfo(connectionId)

	const doToggleClick = useCallback(() => doToggle(connectionId), [doToggle, connectionId])

	const allValues: ConnectionItem[] = useComputed(() => {
		if (!items) return []

		return Array.from(items.entries())
			.map(([id, info]) => {
				if (!info || !info.label) return null
				if (!canAddEntityToFeedbackList(feedbackListType, info)) return null

				return {
					fullId: `${connectionId}:${id}`,
					label: info.label,
					description: info.description,
				}
			})
			.filter((v): v is ConnectionItem => !!v)
	}, [items, feedbackListType])

	const searchResults = filter
		? fuzzySearch(filter, allValues, {
				keys: ['label'],
				threshold: -10_000,
			}).map((x) => x.obj)
		: allValues

	searchResults.sort((a, b) => a.label.localeCompare(b.label))

	if (allValues.length === 0) {
		// Hide card if there are no actions which match
		return null
	} else {
		return (
			<CCard className={'add-browse-card'}>
				<div className="header" onClick={doToggleClick}>
					{connectionInfo?.label || connectionId}
				</div>
				<CCollapse visible={expanded}>
					<CCardBody>
						{searchResults.length > 0 ? (
							<table className="table">
								<tbody>
									{searchResults.map((info) => (
										<AddRow key={info.fullId} info={info} id={info.fullId} doAdd={doAdd} />
									))}
								</tbody>
							</table>
						) : (
							<p className="no-entries">No {itemName} match the search</p>
						)}
					</CCardBody>
				</CCollapse>
			</CCard>
		)
	}
})

interface AddRowProps {
	info: ConnectionItem
	id: string
	doAdd: (itemId: string) => void
}
function AddRow({ info, id, doAdd }: AddRowProps) {
	const doAddClick = useCallback(() => doAdd(id), [doAdd, id])

	return (
		<tr onClick={doAddClick} className="clickable-add-item">
			<td>
				<span className="item-label">{info.label}</span>
				<br />
				{info.description || ''}
			</td>
		</tr>
	)
}
